import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { getPermissionsForRole } from '../utils/rolePermissions';

export interface StaffProfile {
  _id: string;
  name: string;
  email: string;
  role: string;
  permissions: Record<string, boolean>;
  className?: string;
  school?: {
    _id: string;
    name?: string;
    logoUrl?: string;
    logoUpload?: {
      contentType?: string;
      data?: string | { type?: string; data?: number[] };
    };
    activeTermName?: string;
    termDurationMonths?: number;
    activeTermStartDate?: string;
    activeTermEndDate?: string;
    marksSubmissionDeadline?: string;
  } | string;
}

interface StaffContextType {
  staff: StaffProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshStaff: () => Promise<void>;
}

const StaffContext = createContext<StaffContextType | undefined>(undefined);

export const StaffProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [staff, setStaff] = useState<StaffProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const storeToken = (token: string | null) => {
    if (typeof window === 'undefined') return;
    if (token) {
      localStorage.setItem('staff_auth_token', token);
    } else {
      localStorage.removeItem('staff_auth_token');
    }
  };

  const refreshStaff = useCallback(async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('staff_auth_token') : null;
      if (!token) {
        setStaff(null);
        return;
      }
      const response = await api.get('/staff/me');
      const profile = response.data.staff;
      if (profile) {
        profile.permissions = profile.permissions || getPermissionsForRole(profile.role);
        setStaff(profile);
      } else {
        setStaff(null);
        storeToken(null);
      }
    } catch (error) {
      setStaff(null);
      storeToken(null);
    }
  }, []);

  useEffect(() => {
    const hydrate = async () => {
      await refreshStaff();
      setIsLoading(false);
    };
    hydrate();
  }, [refreshStaff]);

  const login = async (email: string, password: string) => {
    const response = await api.post('/staff/login', { email, password });
    const { token, staff: profile } = response.data;
    if (!token || !profile) {
      throw new Error('Invalid staff login response');
    }
    storeToken(token);
    const normalizedStaff = {
      ...profile,
      permissions: profile.permissions || getPermissionsForRole(profile.role),
    };
    setStaff(normalizedStaff);
    navigate('/staff/dashboard');
  };

  const logout = () => {
    storeToken(null);
    setStaff(null);
    navigate('/staff/login');
  };

  return (
    <StaffContext.Provider
      value={{
        staff,
        isLoading,
        isAuthenticated: Boolean(staff),
        login,
        logout,
        refreshStaff,
      }}
    >
      {children}
    </StaffContext.Provider>
  );
};

export const useStaff = () => {
  const context = useContext(StaffContext);
  if (!context) {
    throw new Error('useStaff must be used within a StaffProvider');
  }
  return context;
};
